import React, { useState } from 'react'
import MetaTags from 'react-meta-tags'
import {
  Alert,
  Row,
  Card,
  CardBody,
  Container,
  CardHeader,
  Label,
  Input,
  Col,
} from 'reactstrap'
import MerchantApiDto from '../../../api-dtos/merchant/merchant.dto.interface'
import {
  supportUpdateCashDiscounts,
  supportUpdateSchedule,
} from '../../../helpers/lopay_api_helper'
import { AccountType } from '../../../api-dtos/merchant/merchant-audit-log.dto.interface'

const MerchantCashDiscountsComponent = (props: {
  merchant: MerchantApiDto
  cashDiscountsUpdated: () => void
}) => {
  const { merchant } = props

  const normalizePercentForDisplay = (raw?: number): number => {
    if (typeof raw !== 'number' || Number.isNaN(raw)) return 0
    // If raw looks like a basis-points integer (e.g., 349 -> 3.49)
    if (raw > 100) return raw / 100
    // If raw already looks like a typical percent (1-100), use as-is
    if (raw >= 1) return raw
    // If raw is a fraction (e.g., 0.0349 -> 3.49)
    if (raw > 0 && raw < 1) return raw * 100
    return 0
  }

  const initialEnabled = merchant?.cashDiscounts?.enabled ?? false
  const initialCheckoutMethod =
    merchant?.cashDiscounts?.checkoutMethod ?? 'cash'
  const initialRawDiscountPercent =
    merchant?.cashDiscounts?.discountPercentage ??
    merchant?.cashDiscounts?.flatFeePercentage
  const initialDiscountPercent = Number(
    normalizePercentForDisplay(initialRawDiscountPercent).toFixed(2),
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [enabled, setEnabled] = useState<boolean>(initialEnabled)
  const [checkoutMethod, setCheckoutMethod] = useState<'cash' | 'card'>(
    initialCheckoutMethod,
  )
  const [discountPercent, setDiscountPercent] = useState<number>(
    initialDiscountPercent,
  )
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showEnableWarning, setShowEnableWarning] = useState<boolean>(false)

  if (!merchant) {
    return (
      <Container fluid>
        <Alert color="danger">
          Sorry there was a problem loading the cash discount settings for this
          merchant
        </Alert>
      </Container>
    )
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const needsInstantSwitch =
      enabled &&
      (merchant.payoutSchedule.schedule !== 'instant' ||
        merchant.accountType !== AccountType.Premium)
    let discountsUpdated = false
    try {
      // Update cash discounts first
      await supportUpdateCashDiscounts(merchant.id, {
        isEnabled: enabled,
        checkoutMethod,
        discountPercent: {
          value: Math.round(discountPercent * 100),
          precision: 10000,
        },
      })
      discountsUpdated = true

      // Switch to instant/premium last (triggers email)
      if (needsInstantSwitch) {
        await supportUpdateSchedule(merchant.id, {
          accountType: AccountType.Premium,
          config: { type: 'instant' },
        })
      }

      setSuccessMessage(
        enabled
          ? needsInstantSwitch
            ? 'Cash discounts enabled. Schedule updated to Instant to Bank.'
            : 'Cash discounts enabled.'
          : 'Cash discounts disabled.',
      )
      setTimeout(() => setSuccessMessage(null), 4000)
      props.cashDiscountsUpdated()
    } catch (e: any) {
      console.error('Error saving cash discounts', e)
      // If schedule change failed after discounts were updated, roll back discounts to original values
      if (discountsUpdated) {
        try {
          await supportUpdateCashDiscounts(merchant.id, {
            isEnabled: initialEnabled,
            checkoutMethod: initialCheckoutMethod,
            discountPercent: {
              value: Math.round(initialDiscountPercent * 100),
              precision: 10000,
            },
          })
        } catch (rollbackErr) {
          console.error(
            'Failed to roll back cash discounts after schedule error',
            rollbackErr,
          )
        }
      }
      // Roll back local UI state to original values to reflect that save failed
      setEnabled(initialEnabled)
      setCheckoutMethod(initialCheckoutMethod)
      setDiscountPercent(initialDiscountPercent)
      window.alert(
        e?.message || 'Failed to save cash discounts. No changes were applied.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <React.Fragment>
      <MetaTags>
        <title>Merchant Cash Discount Settings</title>
      </MetaTags>
      <Container fluid>
        {/* Render Breadcrumbs */}
        <Row>
          <Card>
            <CardHeader className="align-items-center d-flex">
              <h4 className="card-title mb-0 flex-grow-1">
                Cash Discount Settings
              </h4>
            </CardHeader>
            <CardBody>
              {successMessage && (
                <Alert color="success">{successMessage}</Alert>
              )}
              <p>
                Cash discounting is a pricing strategy where merchants offer a
                lower price to customers who pay with cash instead of credit or
                debit cards.
              </p>
              {showEnableWarning && (
                <Alert color="danger">
                  Enabling cash discounts for a merchant will put them onto the{' '}
                  <b>'Instant to Bank'</b> schedule with a{' '}
                  <b>3.49% flat fee.</b>
                </Alert>
              )}

              <Row>
                <Label htmlFor="cashDiscountsEnabled">
                  Cash Discounts Status
                </Label>
                <Col sm={12} lg={4}>
                  <div className="input-group">
                    <select
                      className={`form-select`}
                      id="cashDiscountsEnabled"
                      value={enabled ? 'enabled' : 'disabled'}
                      onChange={(e) => {
                        setSuccessMessage(null)
                        const newEnabled = e.target.value === 'enabled'
                        setShowEnableWarning(newEnabled && !enabled)
                        setEnabled(newEnabled)
                      }}
                    >
                      <option key={'enabled'} value={'enabled'}>
                        Enabled
                      </option>
                      <option key={'disabled'} value={'disabled'}>
                        Disabled
                      </option>
                    </select>
                  </div>
                </Col>
              </Row>

              <br />

              <Row>
                <Label htmlFor="cashDiscountsDiscountPercent">
                  Discount Percentage
                </Label>
                <Col sm={12} lg={4}>
                  <div className="input-group">
                    <Input
                      type="number"
                      className="form-control"
                      id="cashDiscountsDiscountPercent"
                      value={discountPercent}
                      onChange={(e) => {
                        setSuccessMessage(null)
                        const next = Number(e.target.value)
                        if (Number.isNaN(next)) {
                          setDiscountPercent(0)
                          return
                        }
                        const clamped = Math.max(0, Math.min(100, next))
                        setDiscountPercent(Number(clamped.toFixed(2)))
                      }}
                      placeholder="0.00"
                      disabled={!enabled}
                      step={0.01}
                    />
                    <div className="input-group-text">%</div>
                  </div>
                </Col>
              </Row>
              <br />
              <Row>
                <Label htmlFor="cashDiscountsCheckoutMethod">
                  Checkout Method
                </Label>
                <Col sm={12} lg={4}>
                  <div className="input-group">
                    <select
                      className={`form-select`}
                      id="cashDiscountsCheckoutMethod"
                      value={checkoutMethod}
                      onChange={(e) => {
                        setSuccessMessage(null)
                        setCheckoutMethod(e.target.value as 'cash' | 'card')
                      }}
                      disabled={!enabled}
                    >
                      <option key={'cash'} value={'cash'}>
                        Cash
                      </option>
                      <option key={'card'} value={'card'}>
                        Card
                      </option>
                    </select>
                  </div>
                </Col>
              </Row>
              <br />
              <Row>
                <Col className="d-flex justify-content-end">
                  <button
                    className="btn btn-primary py-0 pe-2 px-1 d-flex align-items-center justify-content-center"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <i className="mdi mdi-loading mdi-spin font-size-24"></i>
                    ) : (
                      <i className="mdi mdi-content-save font-size-24"></i>
                    )}
                    <span className="ms-1">Save</span>
                  </button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Row>
      </Container>
    </React.Fragment>
  )
}

export default MerchantCashDiscountsComponent
